import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPricingSettings, updatePricingSettings } from "@/lib/estimates.functions";
import { DEFAULT_PRICING, type PricingSettings } from "@/lib/estimates";
import { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw, ArrowLeft, AlertTriangle } from "lucide-react";
import { SectionTabs, AI_ESTIMATOR_TABS } from "@/components/admin/SectionTabs";

export const Route = createFileRoute("/_authenticated/admin/pricing")({
  head: () => ({ meta: [{ title: "Pricing Settings — Admin" }] }),
  component: AdminPricing,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-32 text-center text-zinc-300 bg-[#030303] min-h-screen">
        <p className="mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="py-32 text-center text-zinc-400">Not found.</div>,
});

type Section = { title: string; description?: string; fields: Array<{ key: keyof PricingSettings; label: string; suffix?: string; step?: number }> };

const SECTIONS: Section[] = [
  {
    title: "Interior — walls & ceilings", description: "Per square foot of paintable area.",
    fields: [
      { key: "wall_low", label: "Wall — min", suffix: "$/sq ft", step: 0.1 },
      { key: "wall_high", label: "Wall — max", suffix: "$/sq ft", step: 0.1 },
      { key: "ceiling_low", label: "Ceiling — min", suffix: "$/sq ft", step: 0.1 },
      { key: "ceiling_high", label: "Ceiling — max", suffix: "$/sq ft", step: 0.1 },
    ],
  },
  {
    title: "Trim, baseboards & crown", description: "Per linear foot.",
    fields: [
      { key: "trim_low", label: "Trim — min", suffix: "$/lin ft", step: 0.25 },
      { key: "trim_high", label: "Trim — max", suffix: "$/lin ft", step: 0.25 },
      { key: "baseboard_low", label: "Baseboard — min", suffix: "$/lin ft", step: 0.25 },
      { key: "baseboard_high", label: "Baseboard — max", suffix: "$/lin ft", step: 0.25 },
      { key: "crown_low", label: "Crown — min", suffix: "$/lin ft", step: 0.25 },
      { key: "crown_high", label: "Crown — max", suffix: "$/lin ft", step: 0.25 },
    ],
  },
  {
    title: "Doors, windows & cabinets", description: "Per unit.",
    fields: [
      { key: "door_low", label: "Door — min", suffix: "$/door", step: 5 },
      { key: "door_high", label: "Door — max", suffix: "$/door", step: 5 },
      { key: "window_low", label: "Window — min", suffix: "$/window", step: 5 },
      { key: "window_high", label: "Window — max", suffix: "$/window", step: 5 },
      { key: "cabinet_low", label: "Cabinet — min", suffix: "$/door+drawer", step: 5 },
      { key: "cabinet_high", label: "Cabinet — max", suffix: "$/door+drawer", step: 5 },
    ],
  },
  {
    title: "Exterior, fence, deck",
    fields: [
      { key: "exterior_low", label: "Exterior — min", suffix: "$/sq ft", step: 0.25 },
      { key: "exterior_high", label: "Exterior — max", suffix: "$/sq ft", step: 0.25 },
      { key: "fence_low", label: "Fence — min", suffix: "$/lin ft", step: 0.25 },
      { key: "fence_high", label: "Fence — max", suffix: "$/lin ft", step: 0.25 },
      { key: "deck_low", label: "Deck — min", suffix: "$/sq ft", step: 0.25 },
      { key: "deck_high", label: "Deck — max", suffix: "$/sq ft", step: 0.25 },
      { key: "garage_low", label: "Garage — min", suffix: "$/sq ft", step: 0.25 },
      { key: "garage_high", label: "Garage — max", suffix: "$/sq ft", step: 0.25 },
      { key: "commercial_low", label: "Commercial — min", suffix: "$/sq ft", step: 0.25 },
      { key: "commercial_high", label: "Commercial — max", suffix: "$/sq ft", step: 0.25 },
    ],
  },
  {
    title: "Multipliers", description: "Applied to the per-unit subtotal.",
    fields: [
      { key: "quality_builder", label: "Builder grade ×", step: 0.05 },
      { key: "quality_premium", label: "Premium ×", step: 0.05 },
      { key: "quality_luxury", label: "Luxury ×", step: 0.05 },
      { key: "coats_one", label: "1 coat ×", step: 0.05 },
      { key: "coats_two", label: "2 coats ×", step: 0.05 },
      { key: "coats_three", label: "3 coats ×", step: 0.05 },
      { key: "condition_excellent", label: "Excellent ×", step: 0.05 },
      { key: "condition_good", label: "Good ×", step: 0.05 },
      { key: "condition_fair", label: "Fair ×", step: 0.05 },
      { key: "condition_poor", label: "Poor ×", step: 0.05 },
      { key: "condition_heavy", label: "Heavy repairs ×", step: 0.05 },
    ],
  },
  {
    title: "Markups & production",
    fields: [
      { key: "labor_markup", label: "Labor markup ×", step: 0.05 },
      { key: "material_markup", label: "Material markup ×", step: 0.05 },
      { key: "coverage_per_gallon", label: "Coverage", suffix: "sq ft/gal", step: 10 },
      { key: "production_sqft_per_hour", label: "Production", suffix: "sq ft/hr", step: 10 },
    ],
  },
  {
    title: "Flat adders",
    fields: [
      { key: "travel_fee", label: "Travel fee", suffix: "$", step: 5 },
      { key: "minimum_charge", label: "Minimum service charge", suffix: "$", step: 25 },
    ],
  },
  {
    title: "Range confidence (price spread)",
    description: "Controls how tight or wide the customer's displayed range is around the calculated midpoint. Enter as a decimal — 0.25 = ±25%.",
    fields: [
      { key: "spread_interior", label: "Interior spread (±)", suffix: "decimal", step: 0.05 },
      { key: "spread_exterior", label: "Exterior spread (±)", suffix: "decimal", step: 0.05 },
      { key: "spread_cabinets", label: "Cabinet spread (±)", suffix: "decimal", step: 0.05 },
      { key: "spread_fence_deck", label: "Fence / deck spread (±)", suffix: "decimal", step: 0.05 },
    ],
  },
];

function AdminPricing() {
  const fetchPricing = useServerFn(getPricingSettings);
  const save = useServerFn(updatePricingSettings);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["pricing_settings"],
    queryFn: () => fetchPricing(),
  });

  const [draft, setDraft] = useState<PricingSettings | null>(null);
  useEffect(() => { if (data) setDraft(data); }, [data]);

  const mutation = useMutation({
    mutationFn: (patch: Record<string, number>) => save({ data: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing_settings"] }),
  });

  if (isLoading || !draft) {
    return (
      <div className="py-32 text-center bg-[#030303] min-h-screen text-zinc-400">
        <Loader2 className="size-6 mx-auto animate-spin" />
      </div>
    );
  }

  const set = (k: keyof PricingSettings, v: number) => setDraft({ ...draft, [k]: v });
  const reset = () => setDraft({ ...DEFAULT_PRICING });
  const onSave = () => {
    const patch: Record<string, number> = {};
    for (const sec of SECTIONS) for (const f of sec.fields) patch[f.key] = Number(draft[f.key]);
    mutation.mutate(patch);
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-6"><SectionTabs tabs={AI_ESTIMATOR_TABS} label="AI Estimator" /></div>
        <div className="flex items-end justify-between mb-10">
          <div>
            <h1 className="font-serif text-4xl md:text-5xl mb-2">Pricing Settings</h1>
            <p className="text-zinc-500 text-sm max-w-xl">All values used by the AI Instant Estimate engine. Changes apply to every new estimate immediately.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={reset} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-xs text-zinc-400 hover:text-white"><RotateCcw className="size-3.5" /> Reset to defaults</button>
            <button onClick={onSave} disabled={mutation.isPending} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-300 text-zinc-950 text-xs font-semibold hover:bg-amber-200 disabled:opacity-50">
              {mutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              Save changes
            </button>
          </div>
        </div>

        {mutation.isSuccess && <p className="mb-6 text-xs text-green-300">Saved.</p>}
        {mutation.isError && <p className="mb-6 text-xs text-red-400">{(mutation.error as Error).message}</p>}

        <div className="mb-6 rounded-xl border border-amber-300/20 bg-amber-300/5 p-4 flex gap-3">
          <AlertTriangle className="size-4 text-amber-300 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-100/80 leading-relaxed">
            <span className="text-amber-200 font-medium">Range confidence trade-off.</span> Wider ranges reduce risk but may reduce customer trust. Narrower ranges improve conversion but may require more accurate intake. Recommended defaults: Interior ±25%, Exterior ±35%, Cabinets ±30%, Fence/Deck ±25%.
          </p>
        </div>

        <div className="space-y-6">
          {SECTIONS.map((sec) => (
            <div key={sec.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <h2 className="font-serif text-2xl mb-1">{sec.title}</h2>
              {sec.description && <p className="text-xs text-zinc-500 mb-5">{sec.description}</p>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {sec.fields.map((f) => (
                  <label key={String(f.key)} className="block">
                    <span className="text-[11px] uppercase tracking-widest text-zinc-500 block mb-1.5">{f.label}</span>
                    <div className="relative">
                      <input
                        type="number"
                        step={f.step ?? 0.01}
                        value={draft[f.key]}
                        onChange={(e) => set(f.key, Number(e.target.value))}
                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3.5 py-3 text-[16px] text-white focus:border-amber-300/50 focus:outline-none"
                      />
                      {f.suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-zinc-500">{f.suffix}</span>}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}